/**
 * Realtime → cache wiring that doesn't depend on the transport (Supabase Realtime on the web, the same topics in
 * native): which queries an event on the user's own topic makes stale. Toasts, call prompts and other side effects
 * stay in each app.
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { RealtimeEvents } from '@tradeoutlet/shared';
import { qk } from './keys';

export function userTopicInvalidations(ws: string, event: string, payload: Record<string, unknown>): QueryKey[] {
  const kind = String(payload.kind ?? '');
  switch (event) {
    case RealtimeEvents.NotificationsRead:
      return [qk.notifications(ws)];
    case RealtimeEvents.Notification:
      return [qk.notifications(ws), qk.today(ws), ...(kind.startsWith('workforce.') ? [['wf', ws]] : []), ...(kind === 'task.assigned' ? [qk.myTasks(ws)] : [])];
    // t253u: a call was answered, declined or ended: its "is calling you" bell row is gone.
    case RealtimeEvents.CallUpdated:
      return [qk.notifications(ws)];
    case RealtimeEvents.RecordingUpdated:
      return [['call-recording', String(payload.call_id)], ['recording', String(payload.recording_id)]];
    case RealtimeEvents.MemberAdded:
    case RealtimeEvents.MemberRemoved:
      return [qk.conversations(ws), ...(payload.conversation_id ? [qk.conversation(String(payload.conversation_id))] : [])];
    // t722u: a channel I'm not in was made private or open: it leaves or joins browse and search.
    case RealtimeEvents.ChannelVisibility:
      return [qk.conversations(ws), ['browse', ws], ['channels-browse', ws], ['search'], ...(payload.conversation_id ? [qk.conversation(String(payload.conversation_id))] : [])];
    case RealtimeEvents.ChatSectionsChanged:
      return [qk.chatSections(ws), qk.conversations(ws)];
    // t471u: someone was deleted permanently: names, people lists and chats refetch (their messages read "Deleted user").
    case RealtimeEvents.MemberDeleted:
      return [qk.members(ws), ['members-all', ws], qk.peopleDirectory(ws), ['person', ws], qk.conversations(ws), ['conversation'], ['thread'], ['wf', ws]];
    // t197u: a task I'm on changed somewhere else: every task list, Today and the badges refetch.
    case RealtimeEvents.TaskUpdated:
    case RealtimeEvents.TaskCreated:
    case RealtimeEvents.TaskDeleted:
      return [['tasks-area'], qk.myTasks(ws), ['wf', ws, 'me-today'], ['nav-badges', ws], ...(payload.id ? [qk.task(String(payload.id))] : [])];
    case RealtimeEvents.ShiftsPublished:
    case RealtimeEvents.WorkforceRequestUpdated:
      return [['wf', ws]];
    default:
      return [];
  }
}

/** t187u: the reaction pills cache of one feed item (same key as the web's ItemReactions). */
export const itemReactionsKey = (ws: string, type: string, id: string) => ['item-reactions', ws, type, id] as const;
export interface ItemReactionsPatch { key: QueryKey; reactions: { emoji: string; count: number; user_ids: string[] }[]; at: string }
/**
 * t187u: an `item.reactions` event for this workspace as a cache write (null for anything else). `seen` holds the
 * newest event time per key, so a late, older event (delivery is at-least-once, not ordered) never wins.
 */
export function itemReactionsPatch(ws: string, event: string, payload: Record<string, unknown>, seen: Map<string, string>): ItemReactionsPatch | null {
  if (event !== RealtimeEvents.ItemReactions || payload.workspace_id !== ws) return null;
  const type = String(payload.entity_type ?? ''), id = String(payload.entity_id ?? ''), at = String(payload.at ?? '');
  if (!type || !id || !Array.isArray(payload.reactions)) return null;
  const k = `${type}:${id}`;
  const last = seen.get(k);
  if (last && at && at < last) return null;
  if (at) seen.set(k, at);
  const reactions = (payload.reactions as { emoji: string; count: number; user_ids: string[] }[]).map(r => ({ emoji: r.emoji, count: r.count, user_ids: r.user_ids }));
  return { key: itemReactionsKey(ws, type, id), reactions, at };
}

export function applyInvalidations(qc: QueryClient, keys: QueryKey[]) {
  for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
}
